import * as React from "react"
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  Ellipsis,
  Paperclip,
  Phone,
  PhoneOff,
  Play,
  X,
} from "lucide-react"
import { Link, useNavigate, useParams } from "react-router"

import { ConnectionBanner } from "@/components/chat/connection-banner"
import { EmojiPicker } from "@/components/chat/emoji-picker"
import { MessageRun } from "@/components/chat/message-row"
import { ReplyQuote } from "@/components/chat/reply-quote"
import { UserAvatar } from "@/components/chat/user-avatar"
import { useVoiceControls } from "@/components/chat/use-voice-controls"
import { VoicePanel } from "@/components/chat/voice-panel"
import { Spinner } from "@/components/ui/spinner"
import { Button } from "@/components/ui/button"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@/components/ui/input-group"
import { Marker, MarkerContent, MarkerIcon } from "@/components/ui/marker"
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
} from "@/components/ui/message-scroller"
import { AuthError } from "@/chat/auth"
import {
  buildThread,
  messageAnchorId,
  roomDisplayName,
  type ThreadRow,
} from "@/chat/format"
import {
  IMAGE_MIMES,
  isImageMime,
  isVideoUploadType,
  VIDEO_UPLOAD_TYPES,
  type Attachment,
  type ReplySnapshot,
} from "@/chat/protocol"
import { dismissNotifications } from "@/chat/push"
import { useChat, useTypingNames } from "@/chat/store"
import { cn } from "@/lib/utils"

/** Typing notices are only sent this often while someone keeps typing. */
const TYPING_THROTTLE_MS = 2000

/**
 * A picture or video on its way into the composer. It uploads as soon as it
 * is added, so by the time the person presses send there is usually only an
 * id to attach. The preview is a local `blob:` URL of the original file.
 */
type Upload = {
  key: string
  kind: "image" | "video"
  previewUrl: string
  status: "uploading" | "ready" | "error"
  /** Share of the file sent so far, 0 to 1, while uploading. */
  progress: number
  /** Cancels the upload when the thumbnail is removed mid-flight. */
  abort?: AbortController
  attachment?: Attachment
  error?: string
}

const ACCEPTED_TYPES = [...IMAGE_MIMES, ...VIDEO_UPLOAD_TYPES].join(",")

/**
 * Whether the browser grows a textarea to fit its text on its own.
 * `field-sizing: content` (in the stock Textarea) arrived in Chrome 123;
 * before that the field keeps the height of its `rows` and scrolls inside,
 * so the composer measures and sets the height itself.
 */
const FIELD_SIZING_SUPPORTED =
  typeof CSS !== "undefined" && CSS.supports("field-sizing", "content")

/**
 * Splits a thread into runs: consecutive rows from one person, as marked by
 * `showAuthor`. A run renders as one `MessageGroup` and is the unit the
 * scroller measures and anchors to.
 */
function runsOf(rows: ThreadRow[]): ThreadRow[][] {
  const runs: ThreadRow[][] = []
  for (const row of rows) {
    const current = runs.at(-1)
    if (row.showAuthor || !current) runs.push([row])
    else current.push(row)
  }
  return runs
}

/** Picture and video files out of a paste or a drop; anything else is left alone. */
function mediaFiles(list: DataTransferItemList | FileList | null): File[] {
  if (!list) return []

  const entries: (File | DataTransferItem)[] =
    list instanceof FileList ? Array.from(list) : Array.from(list)

  const files: File[] = []
  for (const entry of entries) {
    const file = entry instanceof File ? entry : entry.getAsFile()
    if (file && /^(image|video)\//.test(file.type)) files.push(file)
  }
  return files
}

export function Thread() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const {
    rooms,
    messages,
    pending,
    members,
    atStart,
    loading,
    user,
    openRoom,
    loadOlder,
    markRead,
    react,
  } = useChat()

  const room = rooms.find((r) => r.id === id)
  const list = React.useMemo(() => messages[id] ?? [], [messages, id])
  const queue = React.useMemo(() => pending[id] ?? [], [pending, id])
  const here = members[id] ?? []
  const typingNames = useTypingNames(id)
  const voiceControls = useVoiceControls(id)

  // The reply being written. It lives here rather than in the composer so a
  // tap on a message far up the thread can start one. The room it was started
  // in is kept with it and the reply is read back only for that room, which
  // drops it on a room switch without an effect that resets state.
  const [reply, setReply] = React.useState<{
    roomId: string
    snapshot: ReplySnapshot
  } | null>(null)
  const replyTo = reply?.roomId === id ? reply.snapshot : null

  const startReply = React.useCallback(
    (snapshot: ReplySnapshot | null) =>
      setReply(snapshot ? { roomId: id, snapshot } : null),
    [id]
  )

  /**
   * Jumps to a quoted message. It is addressed by its DOM id rather than
   * through the scroller, whose items are whole runs: the target is often in
   * the middle of one. A message older than the loaded page is not on screen
   * at all, which the caller learns from the missing element.
   */
  const jumpToMessage = React.useCallback((messageId: string) => {
    const target = document.getElementById(messageAnchorId(messageId))
    if (!target) return

    target.scrollIntoView({ block: "center", behavior: "smooth" })

    // A brief ring, so the eye finds the message the jump landed on.
    target.classList.add("ring-2", "ring-ring", "rounded-lg")
    setTimeout(
      () => target.classList.remove("ring-2", "ring-ring", "rounded-lg"),
      1200
    )
  }, [])

  const rows = React.useMemo(
    () => buildThread(list, queue, user?.id ?? null, user?.name ?? "Вы"),
    [list, queue, user]
  )
  const runs = React.useMemo(() => runsOf(rows), [rows])

  const onReact = React.useCallback(
    (messageId: string, emoji: string) => react(id, messageId, emoji),
    [react, id]
  )

  // Joining the room is what subscribes this client to its presence and
  // history; re-runs when the route changes to another room.
  React.useEffect(() => {
    if (id) openRoom(id)
  }, [id, openRoom])

  // Anything already on screen counts as read, and its system notification
  // has done its job.
  const newest = list.at(-1)?.at
  React.useEffect(() => {
    if (id && newest !== undefined) {
      markRead(id, newest)
      void dismissNotifications(id)
    }
  }, [id, newest, markRead])

  // The scroller keeps the row that was on screen in place while the page
  // of history lands above it.
  function requestOlder() {
    const oldest = list[0]
    if (oldest) loadOlder(id, oldest.id)
  }

  const title = room ? roomDisplayName(room, user?.id ?? null) : id

  // A direct room has one other person, so "who is here" collapses to whether
  // they have it open; a public room lists everyone present.
  const others = here.filter((m) => m.id !== user?.id)
  const subtitle =
    room?.kind === "direct"
      ? others.length > 0
        ? "в сети"
        : "личный чат"
      : here.length > 0
        ? `${here.length} здесь · ${here.map((m) => m.name).join(", ")}`
        : (room?.topic ?? "Здесь пока никого нет")

  return (
    <>
      <header className="flex flex-none items-center gap-2 border-b px-2 py-2">
        {/* The list is already beside the thread on desktop. */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          aria-label="Назад к чатам"
          className="md:hidden"
        >
          <ChevronLeft className="size-5" />
        </Button>

        <Link
          to={`/r/${id}/info`}
          className="flex min-w-0 flex-1 items-center gap-2.5"
          aria-label={`О чате ${title}`}
        >
          <UserAvatar name={title} className="size-8" accent />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{title}</div>
            <div className="truncate text-xs text-muted-foreground">
              {subtitle}
            </div>
          </div>
        </Link>

        {voiceControls.supported && (
          <Button
            variant={voiceControls.inThisRoom ? "destructive" : "ghost"}
            size="icon"
            onClick={() => void voiceControls.toggle()}
            aria-label={
              voiceControls.inThisRoom
                ? "Выйти из голосового чата"
                : "Голосовой чат"
            }
          >
            {voiceControls.inThisRoom ? (
              <PhoneOff className="size-5" />
            ) : (
              <Phone className="size-5" />
            )}
          </Button>
        )}
      </header>

      <VoicePanel roomId={id} controls={voiceControls} />

      {/* On desktop the sidebar's copy of the banner is always in view. */}
      <div className="md:hidden">
        <ConnectionBanner />
      </div>

      {/* Sits outside the transcript: the scroller tells a prepended page of
          history apart from an appended message by watching its first row. */}
      {!atStart[id] && list.length > 0 && (
        <div className="flex flex-none justify-center border-b py-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={requestOlder}
            disabled={loading[id]}
          >
            {loading[id] && <Spinner data-icon="inline-start" />}
            {loading[id] ? "Загрузка…" : "Показать более ранние сообщения"}
          </Button>
        </div>
      )}

      {/* The composer is inside the provider on purpose: sending has to be
          able to scroll the transcript to the newest message. */}
      <MessageScrollerProvider key={id} autoScroll defaultScrollPosition="end">
        <div className="flex min-h-0 flex-1 flex-col">
          <MessageScroller>
            <MessageScrollerViewport aria-label="Сообщения">
              <MessageScrollerContent className="gap-4 px-4 py-4">
                {rows.length === 0 && !loading[id] && (
                  <Empty className="my-auto">
                    <EmptyHeader>
                      <EmptyTitle>Нет сообщений</EmptyTitle>
                      <EmptyDescription>Напишите что-нибудь</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}

                {runs.map((run) => (
                  <MessageScrollerItem key={run[0].key} messageId={run[0].key}>
                    <MessageRun
                      rows={run}
                      selfId={user?.id ?? null}
                      participants={room?.participants ?? []}
                      onReact={onReact}
                      onReply={startReply}
                      onJump={jumpToMessage}
                    />
                  </MessageScrollerItem>
                ))}

                {typingNames.length > 0 && (
                  <MessageScrollerItem>
                    <Marker role="status">
                      <MarkerIcon>
                        <Ellipsis className="animate-pulse" />
                      </MarkerIcon>
                      <MarkerContent>
                        {typingNames.join(", ")}{" "}
                        {typingNames.length === 1 ? "печатает" : "печатают"}
                      </MarkerContent>
                    </Marker>
                  </MessageScrollerItem>
                )}
              </MessageScrollerContent>
            </MessageScrollerViewport>
            <MessageScrollerButton>
              <ArrowDown />
              <span className="sr-only">К последним сообщениям</span>
            </MessageScrollerButton>
          </MessageScroller>
        </div>

        <Composer
          key={id}
          roomId={id}
          title={title}
          replyTo={replyTo}
          onReplyChange={startReply}
        />
      </MessageScrollerProvider>
    </>
  )
}

/**
 * The message box: text, pasted or attached pictures and videos, and the send
 * button. Mounted with the room id as its key, so switching rooms drops the
 * draft and anything attached but not sent — the server sweeps those uploads
 * later.
 */
function Composer({
  roomId,
  title,
  replyTo,
  onReplyChange,
}: {
  roomId: string
  title: string
  /** The message being answered, quoted above the field. */
  replyTo: ReplySnapshot | null
  onReplyChange: (snapshot: ReplySnapshot | null) => void
}) {
  const {
    maxMessageLength,
    maxUploadBytes,
    maxVideoUploadBytes,
    maxAttachments,
    send,
    uploadMedia,
    notifyTyping,
  } = useChat()

  const [draft, setDraft] = React.useState("")
  const [sent, setSent] = React.useState(0)
  const [uploads, setUploads] = React.useState<Upload[]>([])
  const [dragging, setDragging] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const draftRef = React.useRef<HTMLTextAreaElement>(null)

  // Manual auto-grow for browsers without `field-sizing`. It is min-height
  // that is set, not height: the field is a flex item in a column, and there
  // flex-basis decides the height and ignores `height`. Reset first so the
  // field shrinks back when lines are deleted, and clamp to the class's
  // max-height by hand, since min-height would otherwise win over it.
  React.useLayoutEffect(() => {
    if (FIELD_SIZING_SUPPORTED) return
    const field = draftRef.current
    if (!field) return

    field.style.minHeight = ""
    const cap = Number.parseFloat(getComputedStyle(field).maxHeight)
    const wanted = field.scrollHeight
    field.style.minHeight = `${Number.isFinite(cap) ? Math.min(wanted, cap) : wanted}px`
  }, [draft])
  const lastTypingRef = React.useRef(0)

  // Answering a message should put the caret where the answer goes, the same
  // way tapping the field would. Keyed on the id so re-picking the same
  // message after a stray tap elsewhere focuses again.
  React.useEffect(() => {
    if (replyTo) draftRef.current?.focus()
  }, [replyTo])

  /**
   * Your own message must be the one you are looking at. The scroller only
   * follows the thread while it is already at the bottom, so a send from
   * further up would otherwise land off screen.
   *
   * The counter, not the send itself, is what schedules this: React commits
   * the optimistic row and this state in the same pass, so by the time the
   * effect runs the row it should scroll to is in the DOM. The scroller's
   * helpers are kept in a ref because the object they arrive in is rebuilt
   * on every render of the provider.
   */
  const scroller = useMessageScroller()
  const scrollerRef = React.useRef(scroller)
  React.useEffect(() => {
    scrollerRef.current = scroller
  })
  React.useEffect(() => {
    if (sent === 0) return
    scrollerRef.current.scrollToEnd({ behavior: "smooth" })
  }, [sent])

  // Object URLs are not garbage-collected with the component; release whatever
  // is still previewed when the composer goes away, and stop any upload still
  // running — a room switch drops unsent attachments anyway.
  const uploadsRef = React.useRef<Upload[]>([])
  React.useEffect(() => {
    uploadsRef.current = uploads
  }, [uploads])
  React.useEffect(
    () => () => {
      for (const upload of uploadsRef.current) {
        upload.abort?.abort()
        URL.revokeObjectURL(upload.previewUrl)
      }
    },
    []
  )

  const patchUpload = React.useCallback(
    (key: string, patch: Partial<Upload>) =>
      setUploads((current) =>
        current.map((u) => (u.key === key ? { ...u, ...patch } : u))
      ),
    []
  )

  /**
   * Adds pictures and videos to the composer and starts uploading them at
   * once, so the wait happens while the person is still typing the caption
   * rather than after they press send.
   */
  function addFiles(files: File[]) {
    if (files.length === 0) return

    setUploads((current) => {
      const room = Math.max(0, maxAttachments - current.length)
      const accepted = files.slice(0, room)

      const added = accepted.map<Upload>((file) => {
        const key = crypto.randomUUID()
        const previewUrl = URL.createObjectURL(file)
        const kind = file.type.startsWith("video/") ? "video" : "image"
        const base = { key, kind, previewUrl, progress: 0 } as const

        if (!isImageMime(file.type) && !isVideoUploadType(file.type)) {
          return {
            ...base,
            status: "error",
            error:
              kind === "video"
                ? "Только MP4, MOV и WebM."
                : "Только PNG, JPEG, GIF и WebP.",
          }
        }
        const limit = kind === "video" ? maxVideoUploadBytes : maxUploadBytes
        if (file.size > limit) {
          const mb = Math.floor(limit / 1024 / 1024)
          return { ...base, status: "error", error: `Не больше ${mb} МБ.` }
        }

        const abort = new AbortController()
        uploadMedia(file, {
          signal: abort.signal,
          onProgress: (progress) => patchUpload(key, { progress }),
        })
          .then((attachment) =>
            patchUpload(key, { status: "ready", attachment, abort: undefined })
          )
          .catch((error: unknown) => {
            // A removed thumbnail is already gone; nothing to mark.
            if (error instanceof AuthError && error.code === "aborted") return
            patchUpload(key, {
              status: "error",
              abort: undefined,
              error:
                error instanceof AuthError
                  ? error.message
                  : "Не удалось загрузить.",
            })
          })

        return { ...base, status: "uploading", abort }
      })

      return [...current, ...added]
    })
  }

  function removeUpload(key: string) {
    setUploads((current) => {
      const target = current.find((u) => u.key === key)
      if (target) {
        target.abort?.abort()
        URL.revokeObjectURL(target.previewUrl)
      }
      return current.filter((u) => u.key !== key)
    })
  }

  // A pasted screenshot or video file becomes an attachment; a pasted string
  // still lands in the textarea as usual. When the clipboard holds both, the
  // file wins, since the text is then almost always the file's name.
  function onPaste(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = mediaFiles(event.clipboardData.items)
    if (files.length === 0) return

    event.preventDefault()
    addFiles(files)
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault()
    setDragging(false)
    addFiles(mediaFiles(event.dataTransfer.files))
  }

  function onDragOver(event: React.DragEvent) {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return
    event.preventDefault()
    setDragging(true)
  }

  const ready = uploads.filter((u) => u.status === "ready")
  const uploading = uploads.some((u) => u.status === "uploading")
  const canSend = (draft.trim() !== "" || ready.length > 0) && !uploading

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!canSend) return

    // The pending row shows the local preview until the server's copy, which
    // carries the real URL, replaces it.
    const attachments = ready.map((u) => ({
      ...u.attachment!,
      url: u.previewUrl,
    }))

    send(roomId, draft, attachments, replyTo)
    onReplyChange(null)
    setDraft("")
    setSent((count) => count + 1)
    setUploads((current) => current.filter((u) => u.status === "error"))
  }

  function onDraftChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setDraft(event.target.value)

    const now = Date.now()
    if (now - lastTypingRef.current > TYPING_THROTTLE_MS) {
      lastTypingRef.current = now
      notifyTyping(roomId)
    }
  }

  /**
   * Puts an emoji where the caret is, rather than at the end: the picker
   * never takes focus, so the field still holds the selection the person
   * left it at. The caret is moved past the character afterwards so picking
   * several in a row spells them out in order.
   */
  function insertEmoji(char: string) {
    const field = draftRef.current
    const from = field?.selectionStart ?? draft.length
    const to = field?.selectionEnd ?? draft.length

    const next = draft.slice(0, from) + char + draft.slice(to)
    if (next.length > maxMessageLength) return

    setDraft(next)

    const caret = from + char.length
    requestAnimationFrame(() => field?.setSelectionRange(caret, caret))
  }

  // Enter sends; Shift+Enter starts a new line, which is what makes pasting a
  // fenced code block possible.
  function onKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      submit(event)
    }
  }

  return (
    <form
      onSubmit={submit}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={() => setDragging(false)}
      className="flex-none px-3 pt-1 pb-2"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        hidden
        onChange={(event) => {
          addFiles(mediaFiles(event.target.files))
          event.target.value = ""
        }}
      />

      <InputGroup
        className={cn("transition-colors", dragging && "border-ring bg-accent")}
      >
        {replyTo && (
          <InputGroupAddon
            align="block-start"
            className="pb-0"
            onPointerDown={(event) => event.preventDefault()}
          >
            <ReplyQuote
              snapshot={replyTo}
              onCancel={() => onReplyChange(null)}
              className="w-full text-muted-foreground"
            />
          </InputGroupAddon>
        )}

        {uploads.length > 0 && (
          <InputGroupAddon
            align="block-start"
            className="pb-0"
            onPointerDown={(event) => event.preventDefault()}
          >
            <div className="no-scrollbar flex w-full gap-2 overflow-x-auto pt-1 pr-1 pb-1">
              {uploads.map((upload) => (
                <UploadThumb
                  key={upload.key}
                  upload={upload}
                  onRemove={() => removeUpload(upload.key)}
                />
              ))}
            </div>
          </InputGroupAddon>
        )}

        <InputGroupTextarea
          ref={draftRef}
          value={draft}
          onChange={onDraftChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          rows={1}
          maxLength={maxMessageLength}
          enterKeyHint="send"
          autoCapitalize="sentences"
          placeholder={
            ready.length > 0 ? "Добавить подпись…" : `Написать ${title}`
          }
          aria-label={`Сообщение для ${title}`}
          className="no-scrollbar max-h-40 min-h-10 overscroll-contain"
        />

        {/* A tap on a button would move focus off the textarea and drop the
            keyboard on every send; swallowing pointerdown keeps it up, the
            way native messengers do. Click still fires. */}
        <InputGroupAddon
          align="block-end"
          className="p-2"
          onPointerDown={(event) => event.preventDefault()}
        >
          <EmojiPicker onPick={insertEmoji} />

          <InputGroupButton
            size="icon-sm"
            aria-label="Прикрепить фото или видео"
            disabled={uploads.length >= maxAttachments}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip />
          </InputGroupButton>

          <InputGroupButton
            type="submit"
            variant="default"
            size="icon-sm"
            aria-label="Отправить"
            disabled={!canSend}
            className="ml-auto rounded-full"
          >
            <ArrowUp />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </form>
  )
}

/**
 * One picture or video waiting in the composer: preview, progress, and a
 * remove button. A video previews its first frame, marked with a play badge;
 * a large one shows how much of it has gone up.
 */
function UploadThumb({
  upload,
  onRemove,
}: {
  upload: Upload
  onRemove: () => void
}) {
  const previewClass = cn(
    "size-16 rounded-md object-cover",
    upload.status !== "ready" && "opacity-50",
    upload.status === "error" && "ring-2 ring-destructive"
  )

  return (
    <div className="relative flex-none">
      {upload.kind === "video" ? (
        <video
          src={upload.previewUrl}
          muted
          playsInline
          preload="metadata"
          className={cn(previewClass, "bg-black")}
        />
      ) : (
        <img src={upload.previewUrl} alt="" className={previewClass} />
      )}

      {upload.kind === "video" && upload.status === "ready" && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Play className="size-5 fill-white text-white drop-shadow" />
        </div>
      )}

      {upload.status === "uploading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <Spinner className="size-4" />
          {upload.progress > 0 && (
            <span className="text-[10px] font-medium tabular-nums">
              {Math.round(upload.progress * 100)}%
            </span>
          )}
        </div>
      )}

      {upload.status === "error" && (
        <div
          role="alert"
          className="absolute inset-x-0 bottom-0 truncate rounded-b-md bg-destructive/90 px-1 text-[10px] text-white"
          title={upload.error}
        >
          {upload.error}
        </div>
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label={upload.kind === "video" ? "Убрать видео" : "Убрать фото"}
        className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-foreground text-background shadow"
      >
        <X className="size-3" />
      </button>
    </div>
  )
}
